import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { EmbeddedInfiniteScrollList } from '@/components/ui/embedded-infinite-scroll-list';
import { CredentialCardView } from '@/components/secrets/CredentialCardView';
import { useAuthStore } from '@/store/auth';
import { ProtectedRoute } from '@/components/auth/ProtectedRoute';
import { MainLayout } from '@/components/layout/MainLayout';
import { authApi, paymentsApi } from '@/lib/pillarApi';
import {
    type ActiveRoleAssignmentResponse,
    CredentialType,
    getAllRoleAssignments,
    type UserProfileResponse,
    ThirdPartySystemId,
} from '@pillar-portal/api-types';
import { useQuery } from '@tanstack/react-query';
import type { ReactNode } from 'react';
import { z } from 'zod';

const PAYMENT_DEBUG_PAGE_SIZE: number = 25;
export const DashboardOpaqueCursorSchema: z.ZodType<string> = z.string().trim().min(1);

function formatFeeValue(value: number | null, currency: boolean): string {
    if (value === null) {
        return 'not allowed';
    }
    if (currency) {
        return `$${value.toFixed(2)}`;
    }
    return `${value}%`;
}

function renderFeeDefinedByText(
    definedBy: string,
    logicalTerminalReference: string | number,
    processorTid: string,
    merchantAccountReference: string | number,
    processorMid: string
): ReactNode {
    if (definedBy === 'terminal_device') {
        return 'this terminal device';
    }
    if (definedBy === 'logical_terminal') {
        return (
            <>
                logical terminal{' '}
                <a
                    className="text-primary underline"
                    href={`/payments-infrastructure/logical-terminals/${logicalTerminalReference}`}
                >
                    {processorTid}
                </a>
            </>
        );
    }
    if (definedBy === 'merchant_account') {
        return (
            <>
                merchant account{' '}
                <a
                    className="text-primary underline"
                    href={`/payments-infrastructure/merchant-accounts/${merchantAccountReference}`}
                >
                    {processorMid}
                </a>
            </>
        );
    }
    return 'not allowed';
}

function formatQaCheck(label: string, pass: boolean): ReactNode {
    return (
        <div className="text-xs text-muted-foreground">
            {label} <span className={pass ? 'text-emerald-600' : 'text-destructive'}>{pass ? 'PASS' : 'FAIL'}</span>
        </div>
    );
}

function parseDashboardCursorParam(pageParam: unknown): string | undefined {
    if (pageParam === null || pageParam === undefined) {
        return undefined;
    }
    const parseResult = DashboardOpaqueCursorSchema.safeParse(pageParam);
    if (!parseResult.success) {
        throw new Error('Expected opaque cursor');
    }
    return parseResult.data;
}

type DashboardInfinitePanelProps<TListItem> = {
    title: string;
    queryKeyPrefix: readonly unknown[];
    enabled: boolean;
    fetchPage: (params: {
        readonly limit: number;
        readonly cursor?: string;
    }) => Promise<{
        readonly items: ReadonlyArray<TListItem>;
        readonly pagination: {
            readonly has_more: boolean;
            readonly next_cursor?: string;
        };
    }>;
    renderItem: (item: TListItem, index: number) => ReactNode;
};

function DashboardInfinitePanel<TListItem>({
    title,
    queryKeyPrefix,
    enabled,
    fetchPage,
    renderItem,
}: DashboardInfinitePanelProps<TListItem>) {
    return (
        <div className="space-y-2">
            <div className="font-semibold">{title}</div>
            <div className="rounded border p-2">
                <EmbeddedInfiniteScrollList<TListItem, string, readonly unknown[]>
                    queryKeyPrefix={queryKeyPrefix}
                    pageSize={PAYMENT_DEBUG_PAGE_SIZE}
                    maxHeight="30rem"
                    enabled={enabled}
                    fetchPage={async ({ limit, cursor }) => {
                        return await fetchPage({
                            limit,
                            cursor,
                        });
                    }}
                    parseCursorParam={parseDashboardCursorParam}
                    stateCopy={{
                        loadingLabel: 'Loading...',
                        emptyLabel: 'No items.',
                        endOfListLabel: 'End of list.',
                    }}
                    renderItems={({ items }) => (
                        <div className="space-y-1">
                            {items.map((item, index) => (
                                <div key={index} className="text-sm">
                                    {renderItem(item, index)}
                                </div>
                            ))}
                        </div>
                    )}
                />
            </div>
        </div>
    );
}

function DashboardContent() {
    const { user } = useAuthStore();
    const sharedGatewaySystemId: ThirdPartySystemId = ThirdPartySystemId.VALOR_US;
    const activeRoleAssignmentQuery = useQuery<ActiveRoleAssignmentResponse | null>({
        queryKey: ['activeRoleAssignment'],
        queryFn: async () => await authApi.getActiveRoleAssignment(),
        staleTime: 30000,
    });
    const impersonationContextQuery = useQuery({
        queryKey: ['impersonationContext'],
        queryFn: async () => await authApi.getImpersonationContext(),
        enabled: user !== null,
        staleTime: 5000,
    });
    const shouldShowPaymentDebugCards: boolean =
        activeRoleAssignmentQuery.data?.organization_is_merchant === true;
    const impersonationContext = impersonationContextQuery.data ?? null;

    // Determine welcome name: first_name -> last_name -> primary_email
    const getWelcomeName = (userData: Pick<UserProfileResponse, 'first_name' | 'last_name' | 'primary_email'>): string => {
        if (impersonationContext !== null) {
            return impersonationContext.impersonated_primary_email;
        }
        if (userData.first_name) {
            return userData.first_name;
        }
        if (userData.last_name) {
            return userData.last_name;
        }
        return userData.primary_email;
    };

    return (
        <div>
            <h1 className="text-3xl font-bold mb-8">Dashboard</h1>
            {user && (
                <>
                <Card className="max-w-2xl">
                    <CardHeader>
                        <CardTitle>Welcome, {getWelcomeName(user)}!</CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-2">
                        <div>
                            <span className="font-semibold">Primary Email:</span>{' '}
                            {impersonationContext?.impersonated_primary_email ?? user.primary_email}
                        </div>
                        <div>
                            <span className="font-semibold">Roles:</span>{' '}
                            {impersonationContext?.impersonated_role
                                ?? getAllRoleAssignments(user)
                                    .map((role_assignment) => role_assignment.role)
                                    .join(', ')}
                        </div>
                        {impersonationContext === null ? (
                            <>
                                <div>
                                    <span className="font-semibold">Status:</span> {user.status}
                                </div>
                                <div>
                                    <span className="font-semibold">TOTP Enabled:</span>{' '}
                                    {user.totp_enabled ? 'Yes' : 'No'}
                                </div>
                            </>
                        ) : null}
                    </CardContent>
                </Card>
                {shouldShowPaymentDebugCards && (
                <div className="mt-6 space-y-4 max-w-4xl">
                    <h2 className="text-2xl font-semibold">Payments Infrastructure Inventory</h2>
                    <Card>
                        <CardHeader>
                            <CardTitle>Configured Gateway Credentials</CardTitle>
                        </CardHeader>
                        <CardContent className="space-y-4">
                            <DashboardInfinitePanel
                                title="Virtual Terminal Credentials:"
                                queryKeyPrefix={['dashboard', 'info', 'accessible-credentials', 'logical-terminal']}
                                enabled={shouldShowPaymentDebugCards}
                                fetchPage={async ({ limit, cursor }) => {
                                    const response = await paymentsApi.getAccessibleCredentialsInfo({
                                        system_id: sharedGatewaySystemId,
                                        is_virtual: true,
                                        credential_type: CredentialType.VIRTUAL_TERMINAL,
                                        limit,
                                        cursor,
                                    });
                                    return {
                                        items: response.credentials,
                                        pagination: response.pagination,
                                    };
                                }}
                                renderItem={(credential) => (
                                    <CredentialCardView
                                        credential={credential}
                                        showPublicFields={true}
                                    />
                                )}
                            />
                            <DashboardInfinitePanel
                                title="Physical Terminal Credentials:"
                                queryKeyPrefix={['dashboard', 'info', 'accessible-credentials', 'terminal-device']}
                                enabled={shouldShowPaymentDebugCards}
                                fetchPage={async ({ limit, cursor }) => {
                                    const response = await paymentsApi.getAccessibleCredentialsInfo({
                                        system_id: sharedGatewaySystemId,
                                        is_virtual: false,
                                        credential_type: CredentialType.PHYSICAL_TERMINAL,
                                        limit,
                                        cursor,
                                    });
                                    return {
                                        items: response.credentials,
                                        pagination: response.pagination,
                                    };
                                }}
                                renderItem={(credential) => (
                                    <CredentialCardView
                                        credential={credential}
                                        showPublicFields={true}
                                    />
                                )}
                            />
                        </CardContent>
                    </Card>

                    <Card>
                        <CardHeader>
                            <CardTitle>Configured Terminals</CardTitle>
                        </CardHeader>
                        <CardContent className="space-y-4">
                            <DashboardInfinitePanel
                                title="Physical terminal access configurations:"
                                queryKeyPrefix={['dashboard', 'info', 'accessible-terminals', 'physical']}
                                enabled={shouldShowPaymentDebugCards}
                                fetchPage={async ({ limit, cursor }) => {
                                    const response = await paymentsApi.getAccessibleTerminalsInfo({
                                        system_id: sharedGatewaySystemId,
                                        is_virtual: false,
                                        limit,
                                        cursor,
                                    });
                                    return {
                                        items: response.terminals,
                                        pagination: response.pagination,
                                    };
                                }}
                                renderItem={(terminal) => (
                                    <div className="rounded border p-3 space-y-1">
                                        <div>
                                            <a
                                                className="font-medium text-primary underline"
                                                href={`/payments-infrastructure/terminal-devices/${terminal.terminal_device_uuid}`}
                                            >
                                                {terminal.processor_name} / {terminal.processor_mid} / {terminal.processor_tid} /{' '}
                                                {terminal.processor_device_id}
                                            </a>{' '}
                                            <span className="text-xs text-muted-foreground">
                                                (Physical)
                                            </span>
                                        </div>
                                        <div className="text-xs text-muted-foreground">
                                            Effective surcharge:{' '}
                                            {formatFeeValue(terminal.effective_surcharge_pct, false)} defined
                                            by{' '}
                                            {renderFeeDefinedByText(
                                                terminal.effective_surcharge_defined_by,
                                                terminal.logical_terminal_uuid,
                                                terminal.processor_tid,
                                                terminal.merchant_account_uuid,
                                                terminal.processor_mid
                                            )}
                                        </div>
                                        <div className="text-xs text-muted-foreground">
                                            Effective convenience fee:{' '}
                                            {formatFeeValue(terminal.effective_convenience_fee, true)} defined
                                            by{' '}
                                            {renderFeeDefinedByText(
                                                terminal.effective_convenience_fee_defined_by,
                                                terminal.logical_terminal_uuid,
                                                terminal.processor_tid,
                                                terminal.merchant_account_uuid,
                                                terminal.processor_mid
                                            )}
                                        </div>
                                        {formatQaCheck(
                                            'QA: terminal credential link is present:',
                                            terminal.qa_has_credential_link
                                        )}
                                        {formatQaCheck(
                                            'QA: terminal_device.credential_type is set:',
                                            terminal.qa_terminal_device_credential_type_set
                                        )}
                                        {formatQaCheck(
                                            'QA: terminal_device.credential_type matches linked credential:',
                                            terminal.qa_terminal_device_credential_type_matches_linked_credential_type
                                        )}
                                        {formatQaCheck(
                                            'QA: terminal is_virtual matches linked credential type:',
                                            terminal.qa_terminal_virtuality_matches_linked_credential_type
                                        )}
                                    </div>
                                )}
                            />
                            <DashboardInfinitePanel
                                title="Virtual terminal access configurations:"
                                queryKeyPrefix={['dashboard', 'info', 'accessible-terminals', 'virtual']}
                                enabled={shouldShowPaymentDebugCards}
                                fetchPage={async ({ limit, cursor }) => {
                                    const response = await paymentsApi.getAccessibleTerminalsInfo({
                                        system_id: sharedGatewaySystemId,
                                        is_virtual: true,
                                        limit,
                                        cursor,
                                    });
                                    return {
                                        items: response.terminals,
                                        pagination: response.pagination,
                                    };
                                }}
                                renderItem={(terminal) => (
                                    <div className="rounded border p-3 space-y-1">
                                        <div>
                                            <a
                                                className="font-medium text-primary underline"
                                                href={`/payments-infrastructure/terminal-devices/${terminal.terminal_device_uuid}`}
                                            >
                                                {terminal.processor_name} / {terminal.processor_mid} / {terminal.processor_tid} /{' '}
                                                {terminal.processor_device_id}
                                            </a>{' '}
                                            <span className="text-xs text-muted-foreground">
                                                (Virtual)
                                            </span>
                                        </div>
                                        <div className="text-xs text-muted-foreground">
                                            Effective surcharge:{' '}
                                            {formatFeeValue(terminal.effective_surcharge_pct, false)} defined
                                            by{' '}
                                            {renderFeeDefinedByText(
                                                terminal.effective_surcharge_defined_by,
                                                terminal.logical_terminal_uuid,
                                                terminal.processor_tid,
                                                terminal.merchant_account_uuid,
                                                terminal.processor_mid
                                            )}
                                        </div>
                                        <div className="text-xs text-muted-foreground">
                                            Effective convenience fee:{' '}
                                            {formatFeeValue(terminal.effective_convenience_fee, true)} defined
                                            by{' '}
                                            {renderFeeDefinedByText(
                                                terminal.effective_convenience_fee_defined_by,
                                                terminal.logical_terminal_uuid,
                                                terminal.processor_tid,
                                                terminal.merchant_account_uuid,
                                                terminal.processor_mid
                                            )}
                                        </div>
                                        {formatQaCheck(
                                            'QA: terminal credential link is present:',
                                            terminal.qa_has_credential_link
                                        )}
                                        {formatQaCheck(
                                            'QA: terminal_device.credential_type is set:',
                                            terminal.qa_terminal_device_credential_type_set
                                        )}
                                        {formatQaCheck(
                                            'QA: terminal_device.credential_type matches linked credential:',
                                            terminal.qa_terminal_device_credential_type_matches_linked_credential_type
                                        )}
                                        {formatQaCheck(
                                            'QA: terminal is_virtual matches linked credential type:',
                                            terminal.qa_terminal_virtuality_matches_linked_credential_type
                                        )}
                                    </div>
                                )}
                            />
                        </CardContent>
                    </Card>

                    <Card>
                        <CardHeader>
                            <CardTitle>Available Terminal Selections</CardTitle>
                        </CardHeader>
                        <CardContent className="space-y-4">
                            <p className="text-sm text-muted-foreground">
                                The following payment terminals are available to you in the Anchorbase app
                                based on the configuration above.
                            </p>
                            <DashboardInfinitePanel
                                title="Physical terminals:"
                                queryKeyPrefix={['dashboard', 'info', 'joined-terminals', 'physical']}
                                enabled={shouldShowPaymentDebugCards}
                                fetchPage={async ({ limit, cursor }) => {
                                    const response = await paymentsApi.listCardPaymentTerminalDevices({
                                        system_id: sharedGatewaySystemId,
                                        is_virtual: false,
                                        action: 'TAKE_PAYMENT',
                                        limit,
                                        cursor,
                                    });
                                    return {
                                        items: response.terminals,
                                        pagination: response.pagination,
                                    };
                                }}
                                renderItem={(terminal) => (
                                    <div className="rounded border p-3 space-y-2">
                                        <CredentialCardView
                                            credential={{
                                                account_id: terminal.account_id,
                                                system_id: terminal.system_id,
                                                credential_type: terminal.credential_type,
                                                is_active: terminal.credential_is_active,
                                                applies_to_children:
                                                    terminal.credential_applies_to_children,
                                                public_fields: {},
                                            }}
                                            showPublicFields={false}
                                        />
                                        <div>
                                            <a
                                                className="font-medium text-primary underline"
                                                href={`/payments-infrastructure/terminal-devices/${terminal.terminal_device_uuid}`}
                                            >
                                                {terminal.processor_name} / {terminal.processor_mid} / {terminal.processor_tid} /{' '}
                                                {terminal.processor_device_id}
                                            </a>{' '}
                                            <span className="text-xs text-muted-foreground">
                                                (Physical)
                                            </span>
                                        </div>
                                        <div className="text-xs text-muted-foreground">
                                            Effective surcharge:{' '}
                                            {formatFeeValue(terminal.effective_surcharge_pct, false)} defined
                                            by{' '}
                                            {renderFeeDefinedByText(
                                                terminal.effective_surcharge_defined_by,
                                                terminal.logical_terminal_uuid,
                                                terminal.processor_tid,
                                                terminal.merchant_account_uuid,
                                                terminal.processor_mid
                                            )}
                                        </div>
                                        <div className="text-xs text-muted-foreground">
                                            Effective convenience fee:{' '}
                                            {formatFeeValue(terminal.effective_convenience_fee, true)} defined
                                            by{' '}
                                            {renderFeeDefinedByText(
                                                terminal.effective_convenience_fee_defined_by,
                                                terminal.logical_terminal_uuid,
                                                terminal.processor_tid,
                                                terminal.merchant_account_uuid,
                                                terminal.processor_mid
                                            )}
                                        </div>
                                    </div>
                                )}
                            />
                            <DashboardInfinitePanel
                                title="Virtual terminals:"
                                queryKeyPrefix={['dashboard', 'info', 'joined-terminals', 'virtual']}
                                enabled={shouldShowPaymentDebugCards}
                                fetchPage={async ({ limit, cursor }) => {
                                    const response = await paymentsApi.listCardPaymentTerminalDevices({
                                        system_id: sharedGatewaySystemId,
                                        is_virtual: true,
                                        action: 'TAKE_PAYMENT',
                                        limit,
                                        cursor,
                                    });
                                    return {
                                        items: response.terminals,
                                        pagination: response.pagination,
                                    };
                                }}
                                renderItem={(terminal) => (
                                    <div className="rounded border p-3 space-y-2">
                                        <CredentialCardView
                                            credential={{
                                                account_id: terminal.account_id,
                                                system_id: terminal.system_id,
                                                credential_type: terminal.credential_type,
                                                is_active: terminal.credential_is_active,
                                                applies_to_children:
                                                    terminal.credential_applies_to_children,
                                                public_fields: {},
                                            }}
                                            showPublicFields={false}
                                        />
                                        <div>
                                            <a
                                                className="font-medium text-primary underline"
                                                href={`/payments-infrastructure/terminal-devices/${terminal.terminal_device_uuid}`}
                                            >
                                                {terminal.processor_name} / {terminal.processor_mid} / {terminal.processor_tid} /{' '}
                                                {terminal.processor_device_id}
                                            </a>{' '}
                                            <span className="text-xs text-muted-foreground">
                                                (Virtual)
                                            </span>
                                        </div>
                                        <div className="text-xs text-muted-foreground">
                                            Effective surcharge:{' '}
                                            {formatFeeValue(terminal.effective_surcharge_pct, false)} defined
                                            by{' '}
                                            {renderFeeDefinedByText(
                                                terminal.effective_surcharge_defined_by,
                                                terminal.logical_terminal_uuid,
                                                terminal.processor_tid,
                                                terminal.merchant_account_uuid,
                                                terminal.processor_mid
                                            )}
                                        </div>
                                        <div className="text-xs text-muted-foreground">
                                            Effective convenience fee:{' '}
                                            {formatFeeValue(terminal.effective_convenience_fee, true)} defined
                                            by{' '}
                                            {renderFeeDefinedByText(
                                                terminal.effective_convenience_fee_defined_by,
                                                terminal.logical_terminal_uuid,
                                                terminal.processor_tid,
                                                terminal.merchant_account_uuid,
                                                terminal.processor_mid
                                            )}
                                        </div>
                                    </div>
                                )}
                            />
                        </CardContent>
                    </Card>
                </div>
                )}
                </>
            )}
        </div>
    );
}

export default function DashboardPage() {
    return (
        <ProtectedRoute>
            <MainLayout>
                <DashboardContent />
            </MainLayout>
        </ProtectedRoute>
    );
}
